import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'

interface ArticleCoverProps {
    src: string
    alt: string
}

/* A real scroll-driven zoom-out rather than a fade: the plate is oversized on
   entry and settles to 1:1 as the frame reaches its resting position. */
export const ArticleCover: FC<ArticleCoverProps> = ({ src, alt }): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current
        const image = root?.querySelector<HTMLElement>('[data-cover-image]')

        if (!root || !image) {
            return
        }

        if (prefersReducedMotion()) {
            gsap.set(image, { scale: 1 })

            return
        }

        gsap.fromTo(image,
            { scale: 1.25 },
            {
                scale: 1,
                ease: 'none',
                scrollTrigger: {
                    trigger: root,
                    start: 'top bottom',
                    end: 'top 26%',
                    scrub: 0.6
                }
            }
        )
    }, scope, [src])

    return (
        <section
            id='article-cover'
            ref={scope}
            className='mx-auto w-full max-w-[1920px] px-5 lg:px-[33px]'
        >
            <div className='relative aspect-[16/8] w-full overflow-hidden rounded-[var(--radius-frame)] bg-ink-soft'>
                <img
                    data-cover-image
                    src={src}
                    alt={alt}
                    decoding='async'
                    className='absolute inset-0 size-full object-cover will-change-transform'
                    style={{ transform: 'scale(1.25)' }}
                />
            </div>
        </section>
    )
}
